<template>
  <main class="projects-page">
    <!-- Per-page SEO: unique title + meta description (Requirements 10.1). -->
    <SeoHead :seo="seo" />

    <!-- Page header -->
    <section class="projects-header" aria-labelledby="projects-heading">
      <v-container>
        <h1 id="projects-heading" class="projects-heading">Our Projects</h1>
        <p class="projects-intro">
          A selection of ThinkSpatial's surveying, asset capture, monitoring,
          UAV, and spatial software work.
        </p>
      </v-container>
    </section>

    <!-- Project content area: filter + grid / state messages -->
    <section class="projects-content" aria-labelledby="projects-heading">
      <v-container>
        <!-- Loading state. -->
        <div
          v-if="state === 'loading'"
          class="projects-state projects-state--loading"
          role="status"
          aria-live="polite"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            aria-label="Loading projects"
          />
          <p class="projects-state__message">Loading projects…</p>
        </div>

        <!-- Error state: an error message and NO partial/stale cards (Req 6.6). -->
        <div
          v-else-if="state === 'error'"
          class="projects-state projects-state--error"
          role="alert"
        >
          <h2 class="projects-state__heading">We couldn't load the projects</h2>
          <p class="projects-state__message">
            Something went wrong while loading our projects. Please try again.
          </p>
          <v-btn color="primary" variant="flat" @click="loadProjects">
            Try again
          </v-btn>
        </div>

        <!-- Empty state: API returned no website projects (Req 6.5). -->
        <div
          v-else-if="state === 'empty'"
          class="projects-state projects-state--empty"
          role="status"
        >
          <h2 class="projects-state__heading">No projects to show yet</h2>
          <p class="projects-state__message">
            Check back soon — new projects are on the way.
          </p>
        </div>

        <!-- Ready state: filterable grid of project cards (Req 6.2, 6.3). -->
        <template v-else>
          <ServiceCategoryFilter
            v-model="selectedCategory"
            class="projects-filter"
          />

          <!-- No projects match the active filter selection. -->
          <div
            v-if="visibleProjects.length === 0"
            class="projects-state projects-state--empty"
            role="status"
          >
            <h2 class="projects-state__heading">No matching projects</h2>
            <p class="projects-state__message">
              No projects match this category. Try another category.
            </p>
          </div>

          <v-row v-else class="projects-grid">
            <v-col
              v-for="project in visibleProjects"
              :key="project.id"
              cols="12"
              sm="6"
              md="4"
            >
              <router-link
                :to="{ name: 'project-detail', params: { id: project.id } }"
                class="projects-grid__link"
                :aria-label="`View project: ${project.title}`"
              >
                <ProjectCard :project="project" />
              </router-link>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </section>
  </main>
</template>

<script>
// ProjectsPage — the Projects listing page (Requirement 6).
//
// Fetches website projects from the Projects_API via ProjectsApi
// (`listWebsiteProjects`), normalizes the raw payload with `normalizeProjects`
// (total/never-throws so malformed records can't fail the render — Req 11.4),
// and renders a Service_Category-filterable grid of `ProjectCard`s
// (Requirements 6.1, 6.2, 6.3, 11.1).
//
// The page tracks four explicit states — `loading | ready | empty | error`:
//   - loading: request in flight.
//   - ready:   request succeeded with one or more website projects.
//   - empty:   request succeeded with zero website projects (Req 6.5).
//   - error:   request failed; an error message is shown and NO partial or
//              stale project cards are rendered (Req 6.6). The project list is
//              cleared before each fetch so a failed refresh can't leave stale
//              cards behind.
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset (no `<script setup>`).
import ProjectsApi from 'services/ProjectsApi'
import { normalizeProjects } from 'services/projectNormalizer'
import { filterByCategory, ALL_CATEGORIES } from 'services/serviceCategories'
import SeoHead from '@/components/SeoHead.vue'
import ProjectCard from '@/components/ProjectCard.vue'
import ServiceCategoryFilter from '@/components/ServiceCategoryFilter.vue'

export default {
  name: 'ProjectsPage',
  components: {
    SeoHead,
    ProjectCard,
    ServiceCategoryFilter,
  },
  data () {
    return {
      // Explicit render state: 'loading' | 'ready' | 'empty' | 'error'.
      state: 'loading',
      // Normalized website projects (cleared on fetch start and on error so
      // no partial/stale cards can ever be shown — Requirement 6.6).
      projects: [],
      // Active Service_Category filter selection (defaults to `All`).
      selectedCategory: ALL_CATEGORIES,
      // Per-page SEO metadata (Requirement 10.1). Project content itself comes
      // from the API; only this page-level metadata is declared here.
      seo: {
        title: 'Projects — ThinkSpatial',
        description:
          'Browse ThinkSpatial projects across surveying, asset capture, ' +
          'automated monitoring, UAV, and spatial software.',
      },
    }
  },
  computed: {
    // The projects matching the active filter selection. Pure filtering is
    // delegated to the `filterByCategory` helper (Requirement 6.3).
    visibleProjects () {
      return filterByCategory(this.projects, this.selectedCategory)
    },
  },
  created () {
    this.loadProjects()
  },
  methods: {
    // Fetch and normalize website projects, driving the state machine.
    loadProjects () {
      // Reset to loading and clear any previously loaded data up front so a
      // failed (or in-flight) refresh never leaves stale cards visible.
      this.state = 'loading'
      this.projects = []

      return ProjectsApi.listWebsiteProjects()
        .then((response) => {
          const raw =
            (response && response.data && response.data.projects) || []
          const normalized = normalizeProjects(raw)
          this.projects = normalized
          this.state = normalized.length > 0 ? 'ready' : 'empty'
        })
        .catch(() => {
          // On failure: clear data and surface the error state with no cards.
          this.projects = []
          this.state = 'error'
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.projects-header {
  padding: 64px 0 24px;
}

.projects-heading {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #41281b;
  margin-bottom: 12px;
}

.projects-intro {
  font-size: 1.1rem;
  color: #4a4a4a;
  max-width: 60ch;
}

.projects-content {
  padding: 24px 0 64px;
}

.projects-filter {
  margin-bottom: 24px;
}

.projects-grid__link {
  display: block;
  height: 100%;
  text-decoration: none;
  color: inherit;
}

.projects-grid__link:focus-visible {
  outline: 2px solid #e7511e;
  outline-offset: 3px;
  border-radius: 12px;
}

.projects-state {
  text-align: center;
  padding: 48px 0;
}

.projects-state__heading {
  font-size: 1.5rem;
  font-weight: 700;
  color: #41281b;
  margin-bottom: 8px;
}

.projects-state__message {
  font-size: 1rem;
  color: #4a4a4a;
  margin-bottom: 16px;
}

.projects-state--loading .projects-state__message {
  margin-top: 16px;
}
</style>
